// Radha — credit risk: a ceiling per customer, checked against their live
// exposure from collection_accounts. Deliberately reuses the risk_score /
// risk_band / days_past_due already computed there (migration 000/001's
// collections rule engine) rather than inventing a second scoring system —
// this is the preventive half (limit before it's overdue) next to the
// existing reactive half (chase what's already overdue).
import React, { useMemo, useState } from "react";

import { useAccounts } from "../hooks/useCollectionsDirect";
import { useCreditLimits, useSetCreditLimit, creditLimitErrorMessage } from "../hooks/useCreditLimits";
import { riskBandColor } from "../lib/statusColor";
import type { CollectionAccount } from "../types/collections";
import { fmtMoney as inr } from "../lib/money";


const RISK_RANK: Record<string, number> = { critical: 3, high: 2, medium: 1, low: 0 };

interface CustomerExposure {
  customer_name: string; exposure: number; days_past_due: number;
  payment_terms_days: number; risk_band: string;
}

function summarizeByCustomer(accounts: CollectionAccount[]): Map<string, CustomerExposure> {
  const m = new Map<string, CustomerExposure>();
  for (const a of accounts) {
    const cur = m.get(a.debtor_name);
    const out = Number(a.outstanding_amount) || 0;
    if (!cur) {
      m.set(a.debtor_name, {
        customer_name: a.debtor_name, exposure: out, days_past_due: a.days_past_due,
        payment_terms_days: a.payment_terms_days, risk_band: a.risk_band,
      });
    } else {
      cur.exposure += out;
      cur.days_past_due = Math.max(cur.days_past_due, a.days_past_due);
      if ((RISK_RANK[a.risk_band] ?? 0) > (RISK_RANK[cur.risk_band] ?? 0)) cur.risk_band = a.risk_band;
    }
  }
  return m;
}

function LimitCell({ customerName, current }: { customerName: string; current?: string }) {
  const setLimit = useSetCreditLimit();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current ?? "");
  const [note, setNote] = useState<string | null>(null);

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {current ? inr(Number(current)) : <span style={{ color: "var(--fg-faint)" }}>not set</span>}
        <button onClick={() => { setValue(current ?? ""); setEditing(true); }}
          style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
          {current ? "edit" : "set"}
        </button>
      </span>
    );
  }
  const save = () => {
    const n = Number(value);
    if (!(n >= 0)) return;
    setNote(null);
    setLimit.mutate({ customer_name: customerName, credit_limit: n }, {
      onSuccess: () => setEditing(false),
      onError: (e) => setNote(creditLimitErrorMessage(e)),
    });
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <input type="number" value={value} onChange={(e) => setValue(e.target.value)} autoFocus
        style={{ width: 100, padding: "2px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }} />
      <button onClick={save} disabled={setLimit.isPending}
        style={{ padding: "1px 8px", fontSize: 11, border: 0, borderRadius: 5, background: "var(--accent-purple)", color: "#fff", cursor: "pointer" }}>
        {setLimit.isPending ? "…" : "Save"}
      </button>
      <button onClick={() => setEditing(false)} style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>×</button>
      {note && <span style={{ fontSize: 11, color: "var(--status-critical)" }}>{note}</span>}
    </span>
  );
}

export function CreditLimits({ canWrite }: { canWrite: boolean }) {
  const { data: accounts } = useAccounts();
  const { data: limits } = useCreditLimits();

  const rows = useMemo(() => {
    const byCustomer = summarizeByCustomer(accounts ?? []);
    const limitByName = new Map((limits ?? []).map((l) => [l.customer_name, l]));
    // union of everyone with either exposure or an explicit limit, worst exposure first
    const names = new Set([...byCustomer.keys(), ...limitByName.keys()]);
    return [...names].map((name) => {
      const exp = byCustomer.get(name);
      const limit = limitByName.get(name);
      const limitAmt = limit ? Number(limit.credit_limit) : null;
      const overLimit = limitAmt != null && exp ? exp.exposure > limitAmt : false;
      const earlyWarning = exp ? (RISK_RANK[exp.risk_band] ?? 0) >= 2 || exp.days_past_due > exp.payment_terms_days * 1.5 : false;
      return { name, exp, limit, limitAmt, overLimit, earlyWarning };
    }).sort((a, b) => (b.exp?.exposure ?? 0) - (a.exp?.exposure ?? 0));
  }, [accounts, limits]);

  if (rows.length === 0) return null;

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 22 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 10 }}>Credit limits &amp; risk</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
            <th style={{ padding: "6px 8px", fontWeight: 500 }}>Customer</th>
            <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Exposure</th>
            <th style={{ padding: "6px 8px", fontWeight: 500 }}>Credit limit</th>
            <th style={{ padding: "6px 8px", fontWeight: 500 }}>Risk</th>
            <th style={{ padding: "6px 8px", fontWeight: 500 }}>Flags</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} style={{ borderTop: "1px solid var(--border)" }}>
              <td style={{ padding: "8px" }}>{r.name}</td>
              <td style={{ padding: "8px", textAlign: "right", whiteSpace: "nowrap" }}>{r.exp ? inr(r.exp.exposure) : "—"}</td>
              <td style={{ padding: "8px" }}>
                {canWrite ? <LimitCell customerName={r.name} current={r.limit?.credit_limit} /> : (r.limitAmt != null ? inr(r.limitAmt) : <span style={{ color: "var(--fg-faint)" }}>not set</span>)}
              </td>
              <td style={{ padding: "8px", textTransform: "capitalize", color: r.exp ? riskBandColor(r.exp.risk_band as CollectionAccount["risk_band"]) : "var(--fg-muted)" }}>
                {r.exp?.risk_band ?? "—"}
              </td>
              <td style={{ padding: "8px" }}>
                {r.overLimit && <span style={{ fontSize: 11, color: "var(--status-critical)", border: "1px solid var(--status-critical)", borderRadius: 6, padding: "1px 6px", marginRight: 4 }}>over limit</span>}
                {r.earlyWarning && <span style={{ fontSize: 11, color: "var(--status-warning)", border: "1px solid var(--status-warning)", borderRadius: 6, padding: "1px 6px" }}>early warning</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
